import { useEffect, useState } from 'react';
import { ChevronUp, X, Zap, Crown, Key, Shield } from 'lucide-react';
import useScrollDirection from '../hooks/useScrollDirection';

const SECTIONS = [
  { id: 'free-plan-section',     icon: Zap,    color: 'cyan',    label: { es: 'Plan Free',     en: 'Free Plan' } },
  { id: 'premium-plan-section',  icon: Crown,  color: 'amber',   label: { es: 'Plan Premium',  en: 'Premium Plan' } },
  { id: 'advanced-plan-section', icon: Key,    color: 'emerald', label: { es: 'Historial Avanzado', en: 'Advanced History' } },
  { id: 'prohibitions-section',  icon: Shield, color: 'rose',    label: { es: 'Reglas',        en: 'Rules' } },
];

const COLOR_DOT = {
  cyan: 'text-cyan-400',
  amber: 'text-amber-400',
  emerald: 'text-emerald-400',
  rose: 'text-rose-400',
};

/**
 * TOC movil como bottom-sheet colapsable. Solo visible debajo de lg.
 * Tap en la barra inferior abre el sheet con las 4 secciones; tap en una
 * scroll-suave a la seccion y cierra el sheet.
 */
export default function PricingTOCMobile({ language = 'es' }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  // Auto-hide al scroll-down, auto-show al scroll-up. Si el sheet esta abierto
  // se desactiva el auto-hide para no descoordinar la UX.
  const visible = useScrollDirection({ disabled: open });

  // Indicador de progreso de lectura (0..1). Recalcula en cada scroll/resize.
  useEffect(() => {
    const update = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total <= 0) {
        setProgress(0);
        return;
      }
      const ratio = Math.min(1, Math.max(0, window.scrollY / total));
      setProgress(ratio);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Cierra con tecla Escape (accesibilidad)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Bloquear scroll del body solo cuando el sheet esta abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const scrollTo = (id) => {
    const el = document.querySelector(`[data-testid="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  const t = (es, en) => (language === 'es' ? es : en);
  const progressPct = Math.round(progress * 100);

  return (
    <>
      {/* Barra inferior siempre visible en mobile, auto-hide al scroll-down */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40
                   bg-slate-900/95 backdrop-blur-md border-t border-slate-700/60
                   shadow-[0_-8px_24px_rgba(0,0,0,0.35)]
                   transition-transform duration-300 ease-out
                   ${visible ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-center gap-2 text-slate-200 text-sm font-medium py-3
                     pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
          data-testid="pricing-toc-mobile-trigger"
          aria-expanded={open}
        >
          <ChevronUp className="w-4 h-4 text-emerald-400" />
          <span>{t('Navegar sección', 'Jump to section')}</span>
          <span className="ml-2 text-[11px] text-slate-500 tabular-nums" data-testid="pricing-toc-mobile-progress-pct">
            {progressPct}%
          </span>
        </button>
      </div>

      {/* Bottom-sheet */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-50"
          role="dialog"
          aria-modal="true"
          data-testid="pricing-toc-mobile-sheet"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-toc-fade-in"
            onClick={() => setOpen(false)}
          />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700/60
                       rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]
                       animate-toc-slide-up shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white">
                {t('En esta página', 'On this page')}
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                aria-label={t('Cerrar', 'Close')}
                data-testid="pricing-toc-mobile-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex flex-col gap-1.5">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl
                               bg-slate-800/50 hover:bg-slate-800 active:bg-slate-700
                               text-slate-200 text-sm font-medium text-left transition-colors
                               border border-slate-700/40"
                    data-testid={`toc-mobile-link-${s.id}`}
                  >
                    <Icon className={`w-4 h-4 ${COLOR_DOT[s.color]}`} />
                    <span>{s.label[language] || s.label.es}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
